import React, { useMemo, useState } from 'react';
import { Input, useColorModeValue } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import { debounceEvent } from '../../utils/common/component-helpers';

type Props = {
  // Fired with the typed term (debounced) as the user types, and with an empty string when cleared
  onSearch: (searchTerm: string) => void;
};

/**
 * The search bar in the notifications list header which queries notifications by title.
 * Mirrors the site search on the Manage Homes screen — same input element and debounced
 * search-as-you-type loading mechanism.
 */
export function NotificationsSearchInput({ onSearch }: Props) {
  const { t } = useTranslation();
  const backgroundColor = useColorModeValue('customGrey.100', 'customGrey.900');
  const [value, setValue] = useState('');
  const debouncedSearch = useMemo(() => debounceEvent((searchTerm: string) => onSearch(searchTerm)), [onSearch]);

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    setValue(e.currentTarget.value);
    debouncedSearch(e.currentTarget.value);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    // Enter searches immediately without waiting for the debounce (and dismisses the mobile keyboard)
    if (e.key === 'Enter') {
      onSearch(e.currentTarget.value);
      e.currentTarget.blur();
    }
  }

  return (
    <Input
      data-testid="notifications-search-input"
      borderRadius={0}
      type="text"
      data-private
      background={backgroundColor}
      value={value}
      name={t('Common.search')}
      placeholder={t('Notifications.search notifications')}
      onChange={handleInputChange}
      onKeyDown={handleKeyDown}
      enterKeyHint="search"
      size="sm"
      flex={1}
      mx={2}
    />
  );
}
